/**
 * Grid anidado que mide el alto del contenedor.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Responsive } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const MeasuredGrid = ({
    layout = [],
    cols = 8,
    rowHeight = 40,
    margin = [8, 8],
    children,
    isDraggable = false,
    isResizable = false,
    draggableHandle,
    draggableCancel,
    compactType = null,
    resizeHandles = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'],
    onLayoutChange,
    onDrag,
    onDragStop,
    className = 'inner-layout',
    fillContainer = false,
}) => {
    const ref = useRef(null);
    const [box, setBox] = useState({ width: 0, height: 0 });
    const boxRef = useRef({ width: 0, height: 0 });

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const apply = () => {
            const next = {
                width: Math.floor(el.getBoundingClientRect().width),
                height: Math.floor(el.getBoundingClientRect().height),
            };
            if (next.width <= 0) return;
            const prev = boxRef.current;
            if (next.width === prev.width && (!fillContainer || Math.abs(next.height - prev.height) < 2)) return;
            boxRef.current = next;
            setBox(next);
        };
        apply();
        const observer = new ResizeObserver(apply);
        observer.observe(el);
        return () => observer.disconnect();
    }, [fillContainer]);

    const gapY = Array.isArray(margin) ? Number(margin[1] ?? margin[0] ?? 0) : Number(margin || 0);
    const rows = (layout || []).reduce((max, item) => Math.max(max, (item.y || 0) + (item.h || 1)), 0);
    let effectiveRowHeight = Math.max(12, Number(rowHeight) || 40);
    if (fillContainer && box.height > 0 && rows > 0) {
        const usable = box.height - gapY * (rows + 1);
        if (usable > 0) {
            const fitted = Math.floor(usable / rows);
            if (fitted >= 12) effectiveRowHeight = fitted;
        }
    }

    return (
        <div ref={ref} style={styles.host}>
            {box.width > 0 && (
                <Responsive
                    className={className}
                    width={box.width}
                    layouts={{ lg: layout }}
                    breakpoints={{ lg: 0 }}
                    cols={{ lg: cols }}
                    rowHeight={effectiveRowHeight}
                    margin={margin}
                    containerPadding={[0, 0]}
                    compactType={compactType}
                    allowOverlap
                    preventCollision={false}
                    isDraggable={isDraggable}
                    isResizable={isResizable}
                    resizeHandles={resizeHandles}
                    draggableHandle={draggableHandle}
                    draggableCancel={draggableCancel}
                    onLayoutChange={(current) => {
                        if (Array.isArray(current) && current.length > 0 && onLayoutChange) {
                            onLayoutChange(current);
                        }
                    }}
                    onDrag={onDrag}
                    onDragStop={onDragStop}
                    isBounded={false}
                >
                    {children}
                </Responsive>
            )}
        </div>
    );
};

const styles = {
    host: {
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'visible',
        boxSizing: 'border-box'
    }
};

export default MeasuredGrid;
